import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useBookmarks, useBookmark, useHistory, useMeta } from '../api/useManga'
import { mediaUrl } from '../utils/media'
import { Skeleton } from '../components/ui/Skeleton'
import { BOOKMARK_STATUSES as TABS } from '../utils/constants'
import toast from 'react-hot-toast'

const EMPTY_MESSAGES = {
  reading:   'Здесь будет манга, которую ты сейчас читаешь',
  planned:   'Добавляй мангу, которую хочешь прочитать',
  completed: 'Прочитанная манга появится здесь',
  dropped:   'Брошенная манга появится здесь',
}

export default function Bookmarks() {
  const { data: bookmarks, isLoading } = useBookmarks()
  const { data: history = [] } = useHistory()
  const { data: meta } = useMeta()
  const [activeTab, setActiveTab] = useState('reading')

  const historyMap = Object.fromEntries(history.map((h) => [h.manga_id, h]))
  const filtered = bookmarks?.filter((b) => b.status === activeTab) ?? []

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl font-black mb-6">Моя библиотека</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-line">
        {TABS.map((tab) => {
          const count = bookmarks?.filter((b) => b.status === tab.key).length ?? 0
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors relative
                ${activeTab === tab.key
                  ? 'text-accent after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-accent'
                  : 'text-muted hover:text-fg'
                }`}
            >
              {tab.label}
              {count > 0 && (
                <span className="ml-1.5 text-xs text-muted">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => <BookmarkSkeleton key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-base text-muted mb-6">{EMPTY_MESSAGES[activeTab]}</p>
          <Link to="/catalog" className="px-6 py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90 bg-accent text-on-accent">
            Перейти в каталог
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((item) => (
            <BookmarkItem key={item.id} item={item} historyEntry={historyMap[item.manga_id]} meta={meta} />
          ))}
        </div>
      )}
    </div>
  )
}

function BookmarkItem({ item, historyEntry, meta }) {
  const manga = item.manga || item
  const { update, remove } = useBookmark(item.manga_id)
  const mangaUrl = `/manga/${manga.slug || manga.id || item.manga_id}`

  const handleRemove = (e) => {
    e.preventDefault()
    remove.mutate(undefined, {
      onSuccess: () => toast.success('Удалено из библиотеки'),
      onError: () => toast.error('Ошибка'),
    })
  }

  const handleStatusChange = (status) => {
    update.mutate(status, {
      onError: () => toast.error('Ошибка'),
    })
  }

  const continueUrl = historyEntry?.chapter_number && historyEntry?.manga_slug
    ? `/manga/${historyEntry.manga_slug}/chapter/${historyEntry.chapter_number}`
    : null

  return (
    <div className="flex gap-5 p-5 rounded-2xl bg-surface border border-line">
      <Link to={mangaUrl} className="shrink-0">
        <div className="rounded-xl overflow-hidden bg-surface2" style={{ width: 90, aspectRatio: '2/3' }}>
          {manga.cover_url
            ? <img src={mediaUrl(manga.cover_url)} alt={manga.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-3xl text-muted">📖</div>
          }
        </div>
      </Link>

      <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Link to={mangaUrl} className="font-bold text-base leading-snug hover:text-accent transition-colors line-clamp-2">
            {manga.title}
          </Link>

          {manga.author && (
            <p className="text-sm text-muted">{manga.author}</p>
          )}

          <p className="text-sm text-muted">
            {[
              manga.chapters_count != null && `${manga.chapters_count} гл.`,
              manga.status && meta?.statuses?.find(s => s.code === manga.status)?.name,
            ].filter(Boolean).join(' · ')}
          </p>

          {continueUrl && (
            <p className="text-sm text-accent">
              Остановился на главе {historyEntry.chapter_number}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Link to={mangaUrl}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors bg-surface2 text-fg hover:bg-surface3">
            К тайтлу
          </Link>
          {continueUrl ? (
            <Link to={continueUrl}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent">
              Продолжить → гл. {historyEntry.chapter_number}
            </Link>
          ) : manga.chapters_count > 0 && (
            <Link to={mangaUrl}
              className="px-4 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent">
              Начать читать
            </Link>
          )}

          <select
            value={item.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={update.isPending}
            className="ml-auto px-3 py-2 rounded-lg text-sm font-semibold bg-surface2 text-fg border border-line hover:border-accent transition-colors cursor-pointer"
          >
            {TABS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>

          <button
            onClick={handleRemove}
            disabled={remove.isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-red-600/10 text-red-400 border border-red-600/20 hover:bg-red-600/20"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  )
}

function BookmarkSkeleton() {
  return (
    <div className="flex gap-5 p-5 rounded-2xl bg-surface border border-line">
      <Skeleton className="rounded-xl shrink-0" style={{ width: 90, aspectRatio: '2/3' }} />
      <div className="flex-1 flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-2/3 rounded" />
          <Skeleton className="h-4 w-1/3 rounded" />
          <Skeleton className="h-4 w-1/2 rounded" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
      </div>
    </div>
  )
}
